import { ArrowLeftOutlined, CloseOutlined } from '@ant-design/icons';
import { Link, RadioInput } from '@mm/components';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import { EMAIL_MATCH_REGEX } from '../../../../common/constants/validations';
import { useSetNextOnboardingStep } from '../../SignupOnboarding/use-set-next-onboarding-step';
import { ReactComponent as Stars } from '../WebsiteReportSummary/Icons/stars.svg';
import { ReactComponent as Copy } from '../WebsiteReportSummary/Icons/copy.svg';
import styles from './SendToDeveloper.module.css';
import { usePromoterJSCode } from '../../../../editor/features/startBotPromoters/common/use-promoter-js-code';
import { EmailInput } from '../../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import { activeFacebookPageSelector } from 'packages/state/selectors/facebookPageSelector';
import {
  advancedScript,
  getDeveloperInstructions,
  getDeveloperInstructionsAdvanced,
} from './constants';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

type InfoMessageType = 'success' | 'copy' | undefined;

const InfoMessage = ({
  showInfoMessage,
  setShowInfoMessage,
  email,
}: {
  showInfoMessage: InfoMessageType;
  setShowInfoMessage: (type: InfoMessageType) => void;
  email: string;
}) => {
  const { setNextOnboardingStep } = useSetNextOnboardingStep();

  const isAutomations = window?.location?.pathname?.includes('automations');
  const isIntegrations = window?.location?.pathname?.includes('integrations');

  const config = {
    success: {
      header: 'Success!',
      subhader: `We have sent all instructions to ${email}`,
      icon: <Stars className={styles.stars} />,
      secondaryButtonName: 'Send to one more',
      primaryButtonName: 'Go to Dashboard',
      onClick: () => {
        setNextOnboardingStep(OnboardingSignupStep.NONE);
      },
      onSecondaryClick: () => setShowInfoMessage(undefined),
    },
    copy: {
      header: 'Copied to clipboard!',
      subhader: 'Please send this text to your developer. And then...',
      icon: <Copy className={styles.stars} />,
      primaryButtonName: 'Go to Dashboard',
      secondaryButtonName: 'Back',
      onClick: () => {
        setNextOnboardingStep(OnboardingSignupStep.NONE);
      },
      onSecondaryClick: () => setShowInfoMessage(undefined),
    },
  };
  if (!showInfoMessage) {
    return <></>;
  }
  return (
    <>
      {config[showInfoMessage].icon}
      <div className={styles.title}>
        <div className={styles.heading}>{config[showInfoMessage].header} </div>
        <div className={styles.subHeading}>
          {config[showInfoMessage].subhader}
        </div>
      </div>
      <div className={classNames(styles.buttons, styles.centered)}>
        {!isAutomations && !isIntegrations && (
          <PrimaryButton
            className={styles.button}
            onClick={() => config[showInfoMessage].onClick()}
          >
            {config[showInfoMessage].primaryButtonName}
          </PrimaryButton>
        )}

        <PrimaryButton
          className={styles.cancelButton}
          onClick={() => config[showInfoMessage].onSecondaryClick()}
        >
          <ArrowLeftOutlined />
          {config[showInfoMessage].secondaryButtonName}
        </PrimaryButton>
      </div>
    </>
  );
};

export const SendToDeveloper = ({
  promoter,
  onClose,
  onClick,
  advanced,
}: {
  promoter: PromoterModel;
  onClose: () => void;
  onClick?: () => void;
  advanced?: boolean;
}) => {
  const facebookPage = useSelector(
    (state: StoreState) =>
      state.connectedPage.entities[0] || activeFacebookPageSelector(state),
  );
  const [email, setEmail] = useState('');
  const [sendByEmail, setSendByEmail] = useState(true);
  const [showInfoMessage, setShowInfoMessage] =
    useState<InfoMessageType>(undefined);

  const { update } = usePromoterCRUDActions();
  const promoterJSCode = usePromoterJSCode(promoter);

  const updatePromoter = async () => {
    if (promoter) {
      await update(facebookPage.activeBotId, {
        ...promoter,
        sendToDev: email,
      });
      setShowInfoMessage('success');
      onClick?.();
    }
  };
  // tslint:disable max-line-length

  const copyText = getDeveloperInstructions(promoterJSCode);
  const copyTextAdvanced = getDeveloperInstructionsAdvanced(promoterJSCode);
  const { companyName = 'Customers.ai', pixelName = 'X-Ray' } =
    useWhitelabelConfig();

  return (
    <div className={styles.container}>
      <CloseOutlined className={styles.closeIcon} onClick={onClose} />

      {!showInfoMessage && (
        <>
          <div className={styles.title}>
            <div className={styles.heading}>
              Send directions to my developer{' '}
            </div>
          </div>

          <div className={styles.form}>
            <div className={styles.option}>
              <RadioInput
                css={{
                  input: styles.radioInput,
                  labelTitle: classNames(styles.radioInputLabel),
                }}
                name="email"
                checked={sendByEmail}
                value="email"
                label="Via email"
                onClick={() => setSendByEmail(true)}
              />
              {sendByEmail && (
                <>
                  <EmailInput
                    inputProps={{
                      placeholder: '',
                      style: { border: '1px solid #D9D9D9', minHeight: '40px' },
                    }}
                    onChange={p => setEmail(p.email)}
                    className={styles.input}
                    withIcon={false}
                    strict
                  />

                  <PrimaryButton
                    className={styles.button}
                    onClick={() => updatePromoter()}
                    disabled={!email || !EMAIL_MATCH_REGEX.test(email)}
                  >
                    Send
                  </PrimaryButton>
                </>
              )}
            </div>

            <div className={styles.option}>
              <RadioInput
                css={{
                  input: styles.radioInput,
                  labelTitle: classNames(styles.radioInputLabel),
                }}
                name="copy"
                checked={!sendByEmail}
                value="copy"
                label="Copy developer instructions"
                onClick={() => setSendByEmail(false)}
              />
              {!sendByEmail && (
                <>
                  <div className={styles.copyBlock}>
                    <div>Hello,</div>
                    <div>
                      In order to successfully capture details from your
                      anonymous website visitors your javascript must be placed
                      on your website. Your instructions for installing
                      {companyName} {pixelName} website visitor detector are
                      below.
                    </div>
                    <div>
                      Paste the code below immediately after the{' '}
                      <strong>{'<head>'}</strong> tag. You can use the settings
                      in {companyName} to restrict capture on certain pages,
                      users, or actions.
                    </div>
                    <div>
                      Instructions for installing the script can be found{' '}
                      <Link
                        isBlank
                        href="https://customers.ai/help/l/en/article/16hdqqv4tu-x-ray-installation-guide"
                        className={styles.link}
                      >
                        HERE.
                      </Link>
                    </div>
                    <div className={styles.italicCode}>{promoterJSCode}</div>
                    <div>
                      Please note: Shopify users need to install a second
                      additional script in the customer events area of Shopify,
                      that script can be found on your {companyName} Dashboard.
                    </div>
                    {advanced && (
                      <>
                        <div>
                          Add the following code below the existing{' '}
                          <strong>{'<script>'}</strong> tag that includes the
                          X-Ray Website Script
                        </div>
                        <div className={styles.italicCode}>
                          {advancedScript}
                        </div>
                        <br />
                        <div className={styles.boldText}>
                          How to customize the customer journey's code
                        </div>
                        <div>
                          In an event handler (e.g., the click event on a
                          button), you can trigger a customer journey events by
                          making a call to the <strong>xrayData</strong> method
                          with the <strong>page_view</strong> event type
                          together with the desired URL.
                        </div>
                        <div className={styles.boldText}>Examples:</div>
                        <div className={styles.italicCode}>
                          {`document.querySelector('button.add-to-cart').addEventListener('click', function () 
                          { xrayData('page_view', 'https://example.org/use-a-unique-url-here') });`}
                        </div>
                        <div>
                          In this example, we are adding the trigger to an HTML
                          button with the <strong>add-to-cart</strong> CSS
                          class. (e.g.,{' '}
                          {`<button class=”add-to-cart”>Add to Cart</button>`})
                        </div>
                        <div className={styles.italicCode}>
                          {`<button onclick="xrayData('page_view', 'https://example.org/use-a-unique-url-here')”>Add to Cart</button>`}
                        </div>
                        <div>
                          In this example, we are customizing the HTML button to
                          add an <strong>onclick</strong> handler, which
                          triggers a customer journey even when the user clicks
                          on the button.
                        </div>
                        <br />
                        <div className={styles.boldText}>
                          How to customize the contacts attributes
                        </div>
                        <div>
                          To set an attribute, use the{' '}
                          <strong>set_attribute</strong> method of the Xray API,
                          where the two parameters can be any desired attribute
                          name and value, such as:
                        </div>
                        <div className={styles.italicCode}>
                          xrayData('set_attribute', 'attribute_name',
                          'attribute_value')
                        </div>
                        <div className={styles.boldText}>Payload Example:</div>
                        <div>
                          <strong>Headers:</strong>
                        </div>
                        <div className={styles.italicCode}>
                          {`{
  "token": "12312lk3j1lk2j3kl1j2lk31kl2j3kj",
  "other_header": "value",
  "Authorization": "Basic am9obkBleGFtcGxlLmNvbTphYmMxMjM="
}`}
                        </div>
                        <strong>Bodies:</strong>
                        <div className={styles.italicCode}>
                          {`{
  "squadName": "Super hero squad",
  "homeTown": "Metro City",
  "formed": 2016,
  "secretBase": "Super tower",
  "active": true
}`}
                        </div>
                      </>
                    )}
                  </div>

                  <PrimaryButton
                    className={styles.button}
                    onClick={() => {
                      navigator.clipboard.writeText(
                        advanced ? copyTextAdvanced : copyText,
                      );
                      setShowInfoMessage('copy');
                      onClick?.();
                    }}
                  >
                    Copy
                  </PrimaryButton>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {showInfoMessage && (
        <InfoMessage
          showInfoMessage={showInfoMessage}
          setShowInfoMessage={setShowInfoMessage}
          email={email}
        />
      )}
    </div>
  );
};
